import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";
import { sumsubHub } from "@/lib/api";
import { AnswerPill, AuthImg, LabelPill, fmtTs, nice } from "./sumsubShared";

const isPdf = (d) => (d.file_type || "").toLowerCase() === "pdf";

function FullDoc({ applicantId, inspectionId, doc, onClose }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    sumsubHub.imageBlob(applicantId, doc.id, { inspectionId, preview: false }).then((b) => { u = URL.createObjectURL(b); setUrl(u); }).catch(() => setUrl("error"));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [applicantId, inspectionId, doc.id]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="sumsub-doc-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
            {nice(doc.doc_type)}{doc.doc_sub_type ? ` · ${nice(doc.doc_sub_type)}` : ""} <AnswerPill answer={doc.answer} />
            <span className="font-mono text-xs font-normal text-slate-500">{doc.file_name} · {fmtTs(doc.added)}</span>
            {url && url !== "error" && (
              <a href={url} download={doc.file_name || `${doc.id}.${doc.file_type || "bin"}`} className="ml-auto" data-testid="sumsub-doc-download">
                <Button variant="outline" size="sm" className="h-7 text-xs"><Download className="w-3.5 h-3.5 mr-1" />Download</Button>
              </a>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto rounded bg-slate-900/95 p-2 text-center">
          {!url && <div className="py-24 text-sm text-slate-300">Loading…</div>}
          {url === "error" && <div className="py-24 text-sm text-rose-300">Couldn't load this file from Sumsub</div>}
          {url && url !== "error" && (isPdf(doc)
            ? <iframe title={doc.file_name} src={url} className="h-[75vh] w-full rounded bg-white" data-testid="sumsub-doc-pdf" />
            : <img src={url} alt={doc.file_name} className="mx-auto max-h-[78vh] object-contain" data-testid="sumsub-doc-image" />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SumsubDocs({ applicantId, inspectionId, documents }) {
  const [open, setOpen] = useState(null);
  if (!documents?.length) return <p className="text-sm text-slate-400" data-testid="sumsub-docs-empty">No documents uploaded yet.</p>;
  const sorted = [...documents].sort((a, b) => (a.deactivated === b.deactivated ? 0 : a.deactivated ? 1 : -1));
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="sumsub-docs-grid">
      {sorted.map((d) => (
        <button key={d.id} type="button" onClick={() => setOpen(d)} className={`group rounded-lg border bg-white p-2 text-left shadow-sm transition hover:border-slate-400 ${d.deactivated ? "opacity-60" : ""}`} data-testid={`sumsub-doc-${d.id}`}>
          <div className="relative h-36 overflow-hidden rounded bg-slate-100">
            {isPdf(d)
              ? <div className="flex h-full flex-col items-center justify-center text-slate-500"><FileText className="w-8 h-8" /><span className="mt-1 text-[10px] font-mono">PDF</span></div>
              : <AuthImg applicantId={applicantId} imageId={d.id} inspectionId={inspectionId} preview alt={d.doc_type} className="h-full w-full object-cover transition group-hover:scale-105" />}
            <div className="absolute left-1 top-1"><AnswerPill answer={d.answer} /></div>
            {d.deactivated && <span className="absolute right-1 top-1 rounded bg-slate-800/80 px-1 text-[9px] font-semibold uppercase text-white">replaced</span>}
          </div>
          <div className="mt-1.5 text-xs font-medium text-slate-900">{nice(d.doc_type)}{d.country ? ` · ${d.country}` : ""}</div>
          <div className="text-[10px] text-slate-500">{fmtTs(d.added)}{d.source ? ` · ${d.source}` : ""}</div>
          {d.reject_labels?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{d.reject_labels.map((l) => <LabelPill key={l} tone="red">{l}</LabelPill>)}</div>}
        </button>
      ))}
      {open && <FullDoc applicantId={applicantId} inspectionId={inspectionId} doc={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
